'use client'
import { motion, useInView } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SectionHeading from '@/blocks/components/section-heading'
import { PublicStatisticDto } from '@/server/public-api/types'
import { formatCount, statisticDirection } from '@/utils/text'

// Ease-out count-up that starts once the card scrolls into view. Both server
// and client render 0 initially, so hydration matches; the animation only runs
// on the client after mount.
function useCountUp(target: number, active: boolean, duration = 1600) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!active) return
    let raf = 0
    let start = 0
    const step = (now: number) => {
      if (!start) start = now
      const progress = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, active, duration])
  return value
}

const StatCard = ({ stat, index }: { stat: PublicStatisticDto; index: number }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, amount: 0.4 })
  const value = useCountUp(stat.value, inView)
  const { i18n } = useTranslation()

  return (
    <motion.li
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className='rounded-3xl border border-white/10 bg-white/5 px-6 py-10 text-center backdrop-blur-sm'
    >
      <p className='text-4xl font-extrabold tabular-nums text-tertiary-lightFonts md:text-6xl'>
        {/* `<bdi>` isolates prefix + number + suffix from the Arabic paragraph so
            «+500» is not painted as «500+» (`+` is bidi-neutral). The direction
            comes from the CMS affixes (`statisticDirection`), since they may be
            Arabic («ألف»). The number uses the SITE locale (PF-190). */}
        <bdi dir={statisticDirection(stat.prefix, stat.suffix)}>
          {stat.prefix ?? ''}
          {formatCount(value, i18n.language)}
          {stat.suffix ?? ''}
        </bdi>
      </p>
      <p className='mt-3 text-base font-light text-white/90 md:text-xl'>{stat.label}</p>
    </motion.li>
  )
}

// CMS-managed Statistics section (feature 022). Rendered only when the academy
// has published statistics; big animated counters on a translucent band.
const StatisticsSection = ({ statistics }: { statistics: PublicStatisticDto[] }) => {
  const { t } = useTranslation()

  if (!statistics || statistics.length === 0) return null

  return (
    <section id='statistics' className='container scroll-mt-24 space-y-12 py-20'>
      <SectionHeading
        eyebrow={t('statistics.section.eyebrow')}
        title={t('statistics.section.title')}
        subtitle={t('statistics.section.subtitle')}
      />
      <ul className='grid grid-cols-2 gap-6 lg:grid-cols-4'>
        {statistics.map((stat, index) => (
          <StatCard key={stat.id} stat={stat} index={index} />
        ))}
      </ul>
    </section>
  )
}

export default StatisticsSection
